import { HelpLink, Related, ScreenLink } from "./parts";

export function RevenueAndMargin() {
  return (
    <>
      <p>
        <ScreenLink href="/revenue">Revenue &amp; margin</ScreenLink> shows what customers were billed, what their usage
        cost AWS, and the gross profit between the two, by customer, by model and by month, for up to 36 months at a time.
        Every figure can be downloaded as CSV. Billing staff and super admins can open it.
      </p>

      <h2 id="reading-the-chart">Reading the chart and the figures</h2>
      <ul>
        <li>
          Each bar is one month: the grey part is <strong>AWS cost</strong>, the blue part is <strong>gross profit</strong>,
          and the whole bar is what was <strong>billed</strong>. The table under the chart has the same numbers.
        </li>
        <li>
          <strong>Markup %</strong> and <strong>margin %</strong> are shown side by side: profit ÷ cost and profit ÷ billed.
          See <HelpLink to="pricing-and-markup" section="markup-is-not-margin" />.
        </li>
        <li>
          The <strong>cost basis</strong> switch measures cost at AWS list price or on the actual AWS bill. On the actual
          bill, only usage AllCode pays AWS for counts as cost.
        </li>
        <li>
          <strong>Open months are provisional</strong>: they read the same live preview the invoice shows, and change until
          the month is closed. Closed months read the fixed invoices.
        </li>
        <li>
          By model, plan-level lines (fees, minimums, discounts, credits) are grouped under their own row, because they
          belong to no single model.
        </li>
      </ul>

      <h2 id="not-billed">Not billed</h2>
      <p>
        Customers or months that are not billed are left out of billed, cost and profit, and listed separately with their
        AWS list cost and who pays AWS. <strong>Gross profit, all customers</strong> subtracts the AWS cost of those
        not-billed months that AllCode pays for, because that cost is real even though nothing is invoiced.
      </p>
      <table>
        <tbody>
          <tr>
            <td>Profit on billed customers</td>
            <td class="numeric">$28,647.13</td>
          </tr>
          <tr>
            <td>Not billed, AllCode pays AWS</td>
            <td class="numeric">−$1,210.00</td>
          </tr>
          <tr>
            <td>Gross profit, all customers</td>
            <td class="numeric">$27,437.13</td>
          </tr>
        </tbody>
      </table>
      <p>Not-billed usage the customer pays AWS for costs AllCode nothing and is not subtracted.</p>

      <h2 id="forecast">Forecast</h2>
      <p>The forecast projects the current month for each billed customer:</p>
      <ol>
        <li>Month to date: what the month has billed so far.</li>
        <li>Run rate: the average daily price over the last 7 whole UTC days.</li>
        <li>Projected: month to date + run rate × the days left, then the monthly minimum and platform fee.</li>
      </ol>
      <p>
        Example: on 10 October a customer has billed $16.00, its run rate is $2.00 a day and 22 days are left: $16.00 + 22 ×
        $2.00 = $60.00 of usage. That clears its $50 minimum, and its $5 fee makes the projection $65.00. Late usage and
        adjustments are not projected.
      </p>

      <Related to={["pricing-and-markup", "aws-costs", "rate-plans", "closing-a-month"]} />
    </>
  );
}
